import { supabase } from '../lib/supabaseClient';
import { getCurrentEmployee } from './pinAuthService';

export interface CompAllocation {
  id: string;
  employee_id: string;
  drinks: number;
  food: number;
  allocated_at: string;
  allocated_by?: string;
  expires_at: string;
  notes?: string;
}

export interface CompRedemption {
  id: string;
  employee_id: string;
  allocation_id: string;
  type: 'drink' | 'food';
  redeemed_at: string;
  notes?: string;
  employee?: {
    id: string;
    name: string;
  };
}

export interface EmployeeBalance {
  drinks_available: number;
  food_available: number;
}

/**
 * Allocate comps to an employee
 */
export async function allocateComps(
  employeeId: string,
  drinks: number,
  food: number,
  expiresAt: string,
  notes?: string
): Promise<{ data: CompAllocation | null; error: any }> {
  const admin = getCurrentEmployee();

  const { data, error } = await supabase
    .from('comp_allocations')
    .insert({
      employee_id: employeeId,
      drinks,
      food,
      expires_at: expiresAt,
      allocated_by: admin?.id || null,
      notes: notes || null,
    })
    .select()
    .single();

  if (error) {
    console.error('[Comp Service] Error allocating comps:', error);
  } else {
    console.log('[Comp Service] Allocated:', drinks, 'drinks,', food, 'food to', employeeId);
  }

  return { data, error };
}

/**
 * Get employee balance (available comps)
 */
export async function getEmployeeBalance(employeeId: string): Promise<{ data: EmployeeBalance; error: any }> {
  const { data, error } = await supabase
    .rpc('get_employee_comps', { p_employee_id: employeeId });

  if (error) {
    console.error('[Comp Service] Error fetching balance:', error);
    return { data: { drinks_available: 0, food_available: 0 }, error };
  }

  const balance = data?.[0] || { drinks_available: 0, food_available: 0 };
  return { data: balance, error: null };
}

/**
 * Get all allocations for an employee
 */
export async function getEmployeeAllocations(employeeId: string): Promise<{ data: CompAllocation[]; error: any }> {
  const { data, error } = await supabase
    .from('comp_allocations')
    .select('*')
    .eq('employee_id', employeeId)
    .gt('expires_at', new Date().toISOString())
    .order('expires_at', { ascending: true });

  if (error) {
    console.error('[Comp Service] Error fetching allocations:', error);
  }

  return { data: data || [], error };
}

/**
 * Redeem a comp
 * Finds allocation with available comps and creates redemption record
 */
export async function redeemComp(
  employeeId: string,
  type: 'drink' | 'food'
): Promise<{ data: CompRedemption | null; error: any }> {
  console.log('[Comp Service] Redeeming', type, 'for employee:', employeeId);

  // First get all unexpired allocations
  const { data: allocations, error: allocError } = await supabase
    .from('comp_allocations')
    .select('*')
    .eq('employee_id', employeeId)
    .gt('expires_at', new Date().toISOString());

  if (allocError) {
    console.error('[Comp Service] Error fetching allocations:', allocError);
    return { data: null, error: allocError };
  }

  if (!allocations || allocations.length === 0) {
    return { data: null, error: { message: 'No comp allocations found for this employee' } };
  }

  // For each allocation, check if it has available comps of the requested type
  for (const allocation of allocations) {
    // Count existing redemptions for this allocation
    const { data: redemptions, error: redemptionsError } = await supabase
      .from('comp_redemptions')
      .select('id')
      .eq('allocation_id', allocation.id)
      .eq('type', type);

    if (redemptionsError) {
      console.error('[Comp Service] Error counting redemptions:', redemptionsError);
      continue;
    }

    const usedCount = redemptions?.length || 0;
    const availableCount = type === 'drink' ? allocation.drinks : allocation.food;

    if (usedCount < availableCount) {
      // This allocation has available comps - redeem from it
      const { data: redemption, error: redeemError } = await supabase
        .from('comp_redemptions')
        .insert({
          employee_id: employeeId,
          allocation_id: allocation.id,
          type,
          redeemed_at: new Date().toISOString(),
        })
        .select(`
          *,
          employee:employees(id, name)
        `)
        .single();

      if (redeemError) {
        console.error('[Comp Service] Error creating redemption:', redeemError);
        return { data: null, error: redeemError };
      }

      console.log('[Comp Service] Redeemed successfully:', type);
      return { data: redemption, error: null };
    }
  }

  // No allocation has available comps of this type
  return { data: null, error: { message: `No ${type} comps available` } };
}

/**
 * Get redemption history
 */
export async function getRedemptionHistory(employeeId?: string, limit = 50): Promise<{ data: CompRedemption[]; error: any }> {
  let query = supabase
    .from('comp_redemptions')
    .select(`
      *,
      employee:employees(id, name)
    `)
    .order('redeemed_at', { ascending: false })
    .limit(limit);

  if (employeeId) {
    query = query.eq('employee_id', employeeId);
  }

  const { data, error } = await query;

  if (error) {
    console.error('[Comp Service] Error fetching history:', error);
  }

  return { data: data || [], error };
}

/**
 * Get redemption stats for admin dashboard
 */
export async function getRedemptionStats(): Promise<{
  data: { totalRedemptions: number; todayRedemptions: number; activeEmployees: number };
  error: any;
}> {
  const today = new Date().toISOString().split('T')[0];

  // Get today's redemptions
  const { data: todayData, error: todayError } = await supabase
    .from('comp_redemptions')
    .select('id', { count: 'exact' })
    .gte('redeemed_at', `${today}T00:00:00`)
    .lte('redeemed_at', `${today}T23:59:59`);

  // Get total redemptions
  const { data: totalData, error: totalError } = await supabase
    .from('comp_redemptions')
    .select('id', { count: 'exact' });

  // Get active employees (with unexpired allocations)
  const { data: employeeData, error: employeeError } = await supabase
    .from('comp_allocations')
    .select('employee_id')
    .gt('expires_at', new Date().toISOString());

  const errors = [todayError, totalError, employeeError].filter(Boolean);
  if (errors.length > 0) {
    return {
      data: { totalRedemptions: 0, todayRedemptions: 0, activeEmployees: 0 },
      error: errors[0],
    };
  }

  // Count unique employees from allocations
  const uniqueEmployees = employeeData
    ? new Set(employeeData.map((a: any) => a.employee_id)).size
    : 0;

  return {
    data: {
      totalRedemptions: totalData?.length || 0,
      todayRedemptions: todayData?.length || 0,
      activeEmployees: uniqueEmployees,
    },
    error: null,
  };
}

/**
 * Revoke comps from an employee (creates negative allocation)
 */
export async function revokeComps(
  employeeId: string,
  drinks: number,
  food: number,
  notes?: string
): Promise<{ data: CompAllocation | null; error: any }> {
  const admin = getCurrentEmployee();

  // Create a negative allocation to revoke comps
  const { data, error } = await supabase
    .from('comp_allocations')
    .insert({
      employee_id: employeeId,
      drinks: -Math.abs(drinks), // Always negative
      food: -Math.abs(food),     // Always negative
      allocated_by: admin?.id || null,
      notes: notes || 'Revoked by admin',
      // Set expiration to 24 hours from now so it reduces the balance immediately
      expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
    })
    .select()
    .single();

  if (error) {
    console.error('[Comp Service] Error revoking comps:', error);
  } else {
    console.log('[Comp Service] Revoked:', drinks, 'drinks,', food, 'food from', employeeId);
  }

  return { data, error };
}
